'use client';
import { useState, useId } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import NextImage from 'next/image';
import { type ImageField } from 'lib/types';
import { cn } from 'lib/utils';
import { trackEvent } from 'lib/analytics';
import {
  type BloomProps,
  Bloom,
  HeaderSection,
  Icon,
  Link,
  RichText,
  Wrapper,
} from 'ui';

export type TabsProps = {
  className?: string;
  title: string;
  intro?: string;
  tabs: {
    label: string;
    title: string;
    intro: string;
    content?: string; // rich text
    image?: ImageField;
    alignment: 'row' | 'column';
  }[];
  bloom1?: BloomProps;
  bloom2?: BloomProps;
  bloom3?: BloomProps;
};

const TABS_EVENT_NAME = 'product_tab_clicked';
const TABS_LOCATION = 'body_tabs_section';

const Tabs = ({
  className,
  title,
  intro,
  tabs,
  bloom1,
  bloom2,
  bloom3,
}: TabsProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const [expandedMobileCards, setExpandedMobileCards] = useState<Set<number>>(
    new Set()
  );
  const tabsId = useId();

  const fireTabTracking = (
    tabTitle: string,
    index: number,
    action: 'open' | 'close'
  ) => {
    trackEvent(TABS_EVENT_NAME, {
      cta: tabTitle,
      location: TABS_LOCATION,
      action: `${action}_tab`,
      tabIndex: index,
      tabName: tabTitle,
    });
  };

  const toggleMobileCard = (index: number) => {
    const isCurrentlyExpanded = expandedMobileCards.has(index);
    const newExpanded = new Set(expandedMobileCards);

    // Analytics tracking for mobile card
    fireTabTracking(
      tabs?.[index]?.label || '',
      index,
      isCurrentlyExpanded ? 'close' : 'open'
    );

    if (isCurrentlyExpanded) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedMobileCards(newExpanded);
    setActiveTab(index); // Sync with desktop active tab
  };

  const handleDesktopTabClick = (index: number) => {
    const isSameTab = activeTab === index;

    // Only track if the tab selection changes
    if (!isSameTab) {
      fireTabTracking(tabs?.[index]?.label || '', index, 'open');
    }

    setActiveTab(index);
    // Sync with mobile: expand the corresponding mobile card
    setExpandedMobileCards((prev) => new Set(prev).add(index));
  };

  return (
    <Wrapper
      as="section"
      className={cn('relative flex flex-col gap-12 lg:gap-18', className)}
    >
      <Bloom {...bloom1} />
      <Bloom {...bloom2} />
      <Bloom {...bloom3} />

      <HeaderSection
        content={intro}
        alignment="center"
        className="relative z-2"
        {...{ title }}
      />

      {/* Mobile: stacked cards */}
      <div className="relative z-2 block lg:hidden space-y-14">
        {tabs.map((tab, index) => {
          const isExpanded = expandedMobileCards.has(index);
          return (
            <article
              key={index}
              className={cn(
                'relative z-2 bg-graphite-900/30 py-8 px-6 rounded-lg transition-colors duration-300'
              )}
            >
              <h3 className="text-h4 mb-6">{tab.title}</h3>

              {!!tab.image && (
                <NextImage
                  src={tab.image.src}
                  alt={tab.image.alt}
                  width={tab.image.width}
                  height={tab.image.height}
                  className="w-full h-auto max-w-150 mb-6 rounded-lg overflow-hidden"
                />
              )}

              <p className="text-neutral-200 mb-6">{tab.intro}</p>

              {!!tab.content && (
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                      animate={{
                        opacity: 1,
                        height: 'auto',
                        marginBottom: 24, // 6 * 4px (mb-6)
                      }}
                      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                      transition={{
                        duration: 0.4,
                        ease: [0.04, 0.62, 0.23, 0.98], // Custom bezier for buttery smoothness
                        opacity: { duration: 0.25 },
                      }}
                      style={{
                        overflow: 'hidden',
                        willChange: 'height, opacity, margin-bottom',
                      }}
                    >
                      <RichText
                        content={tab.content}
                        theme="neutral"
                        variant="condensed"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

              {!!tab.content && (
                <div className="border-t border-clay-50/15 pt-6">
                  <Link
                    onClick={() => toggleMobileCard(index)}
                    className="flex items-center gap-2"
                  >
                    <span>Show {isExpanded ? 'Less' : 'More'}</span>
                    <Icon
                      name={isExpanded ? 'minus' : 'plus'}
                      className="fill-clay-50 w-3 h-auto shrink-0"
                    />
                  </Link>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {/* Desktop: tabs*/}
      <div className="hidden lg:block relative z-2">
        <div className="relative">
          <ul
            className="h-16 flex border-b border-clay-50 relative"
            role="tablist"
            aria-label={title}
          >
            {tabs.map((tab, index) => (
              <li
                key={index}
                className="flex-1 flex relative"
                role="presentation"
              >
                <Link
                  variant="primary-white"
                  className={cn(
                    'relative z-10 w-full text-center no-underline focus-visible:outline-0 focus-visible:text-stroke-clay-50',
                    {
                      'text-stroke-clay-50': activeTab === index,
                    }
                  )}
                  onClick={() => {
                    handleDesktopTabClick(index);
                  }}
                  role="tab"
                  aria-selected={activeTab === index}
                  aria-controls={`${tabsId}-tabpanel-${index}`}
                  id={`${tabsId}-tab-${index}`}
                >
                  {tab.label}
                </Link>
              </li>
            ))}

            {/* Animated underline */}
            <motion.div
              className="absolute bottom-0 h-0.5 bg-clay-50"
              initial={false}
              animate={{
                left: `${(activeTab / tabs.length) * 100}%`,
                width: `${100 / tabs.length}%`,
              }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
              }}
              aria-hidden="true"
            />
          </ul>
        </div>

        {/* Tab content */}
        <div className="pt-18">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut',
              }}
              role="tabpanel"
              id={`${tabsId}-tabpanel-${activeTab}`}
              aria-labelledby={`${tabsId}-tab-${activeTab}`}
              tabIndex={0}
            >
              <div
                className={cn({
                  'max-w-180 mx-auto flex flex-col gap-16 text-center':
                    tabs?.[activeTab]?.alignment === 'column',
                  'flex gap-26 items-center justify-between':
                    tabs?.[activeTab]?.alignment === 'row',
                })}
              >
                <div className="flex flex-col gap-6">
                  <h3 className="text-h4">{tabs?.[activeTab]?.title}</h3>
                  <p className="text-neutral-200">{tabs?.[activeTab]?.intro}</p>
                  {!!tabs?.[activeTab]?.content && (
                    <RichText
                      content={tabs[activeTab].content}
                      theme="neutral"
                      variant="condensed"
                    />
                  )}
                </div>

                {!!tabs?.[activeTab]?.image && (
                  <NextImage
                    src={tabs[activeTab].image.src}
                    alt={tabs[activeTab].image.alt}
                    width={tabs[activeTab].image.width}
                    height={tabs[activeTab].image.height}
                    className={cn('relative rounded-lg overflow-hidden', {
                      'mx-auto w-full h-auto':
                        tabs[activeTab].alignment === 'column',
                      'shrink-0 max-w-[55%] h-auto':
                        tabs[activeTab].alignment === 'row',
                    })}
                  />
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Wrapper>
  );
};

export default Tabs;
